import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { useRouter } from 'next/navigation';
import { useTheme } from '@mui/material/styles';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import {
  Typography,
  Chip,
  Button,
  IconButton,
  MobileStepper,
  Avatar as MUIAvatar,
  Fade,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Catalog as CatalogDict } from '@/shared/i18n';
import { CatalogSliderItem } from '../../../types/catalog-view-data';

interface SwipableCatalogCarouselProps {
  dict: CatalogDict;
  sliderItems: CatalogSliderItem[];
}

const CarouselContent = forwardRef<
  HTMLDivElement,
  { item: CatalogSliderItem; style?: React.CSSProperties }
>(({ item, style }, ref) => {
  const router = useRouter();
  if (!item) return null;

  return (
    <div ref={ref} style={style} className="flex flex-col justify-center flex-1 px-8 py-10 shrink">
      <div className="flex flex-col w-full gap-4">
        <Chip
          label={
            <Typography variant="button-s" color="text-primary">
              {item.tag}
            </Typography>
          }
          avatar={
            <MUIAvatar
              src="/assets/images/catalog/dot.png"
              style={{ width: '16px', height: '16px', marginRight: '0px' }}
            />
          }
          variant="filled"
          size="medium"
          className="self-center bg-error-main"
        />
        <Typography variant="mac-sans-h2" className="mt-4" component="h2">
          {item.title}
        </Typography>
        <img
          src="/assets/images/catalog/separator.svg"
          className="w-[80px] mx-auto"
          onLoad={() => {}}
        />
        <Typography variant="s1" component="h3">
          {item.subtitle}
        </Typography>
      </div>
      <div className="flex flex-col self-center max-w-full mt-8">
        {!!item.date && (
          <div className="flex items-center justify-center w-full gap-2">
            <img
              loading="lazy"
              src="assets/images/catalog/calendar-today.png"
              alt="calendar"
              className="w-4"
            />
            <Typography variant="body-small-500" className="self-stretch my-auto">
              {item.date}
            </Typography>
          </div>
        )}
        <Button
          size="large"
          variant="contained"
          color="primary"
          className="w-full mt-4"
          onClick={() => router.push(item.pageSlug)}
        >
          <Typography variant="button-l">{item.buttonLabel}</Typography>
        </Button>
      </div>
    </div>
  );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselImage: React.FC<{ imageUrl: string }> = ({ imageUrl }) => (
  <div
    className="min-h-[250px] md:min-h-[450px]"
    style={{
      backgroundImage: `url(${imageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      height: '100%',
    }}
  />
);

export const SwipableCatalogCarousel: React.FC<SwipableCatalogCarouselProps> = ({
  dict,
  sliderItems,
}) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  const maxSteps = sliderItems?.length || 0;
  const currentItem = sliderItems[activeStep];
  const imageUrl = `${currentItem?.image}`;

  const updateHeight = () => {
    const heights = contentRefs.current.map((ref) => ref?.getBoundingClientRect().height || 0);
    const max = Math.max(...heights);
    setMaxHeight(max);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      updateHeight();
    }, 500);
    return () => clearTimeout(timeout);
  }, [sliderItems]);

  if (!sliderItems) return null;

  return (
    <div className="flex flex-col justify-center w-full mt-4">
      <div
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: 0,
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        {sliderItems.map((item, idx) => (
          <div key={idx} style={{ display: 'block' }}>
            <CarouselContent
              item={item}
              ref={(el) => {
                contentRefs.current[idx] = el;
              }}
            />
          </div>
        ))}
      </div>

      <Grid
        container
        spacing={0}
        className="bg-paper rounded-xl text-center overflow-hidden h-full lg:h-auto"
      >
        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 2, md: 1 }}>
          <CarouselContent
            item={currentItem}
            ref={(el) => {
              contentRefs.current[activeStep] = el;
            }}
            style={{ minHeight: maxHeight || undefined }}
          />
        </Grid>
        <Grid
          size={{ xs: 12, md: 6 }}
          className="overflow-hidden h-[100px] md:h-full"
          order={{ xs: 1, md: 2 }}
        >
          <Fade in={true} timeout={500} key={imageUrl}>
            <div>
              <CarouselImage imageUrl={imageUrl} />
            </div>
          </Fade>
        </Grid>
      </Grid>

      <div className="flex items-center justify-center w-full mt-2">
        <MobileStepper
          variant="dots"
          steps={maxSteps}
          position="static"
          activeStep={activeStep}
          nextButton={
            <IconButton
              size="large"
              color="primary"
              onClick={() => setActiveStep((prev) => Math.min(prev + 1, maxSteps - 1))}
              disabled={activeStep === maxSteps - 1}
            >
              {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
          }
          backButton={
            <IconButton
              size="large"
              color="primary"
              onClick={() => setActiveStep((prev) => Math.max(prev - 1, 0))}
              disabled={activeStep === 0}
            >
              {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
          }
          className="gap-3"
        />
      </div>
    </div>
  );
};
